import React from "react";
import { Link } from "react-router-dom";
import { Star, Heart, Trophy } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useCart, useAuth } from "@/lib/store";
import { toast } from "sonner";
import { shopApi } from "@/lib/api";

export default function ProductCard({ product }) {
  const { add } = useCart();
  const { user } = useAuth();
  const [wish, setWish] = React.useState(false);
  const [popping, setPopping] = React.useState(false);
  const { data: bestSellers } = useQuery({
    queryKey: ["bestSellerIds"],
    queryFn: shopApi.bestSellerIds,
    staleTime: 10 * 60 * 1000,
  });
  const isBestSeller = !!bestSellers?.ids?.includes(product.product_id);

  const toggleWish = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!user) { toast.error("Sign in to save to wishlist"); return; }
    try { await shopApi.toggleWishlist(product.product_id); setWish((v) => !v); toast.success(wish ? "Removed from wishlist" : "Saved to wishlist"); } catch { toast.error("Failed"); }
  };

  const handleAdd = (e) => {
    e.preventDefault();
    add(product, 1, null, null, false);
    toast.success("Added to cart", { description: "Keep browsing — open the cart when you're ready." });
    // Haptic tap on supported devices (iOS Safari ignores it, Android buzzes ~10ms)
    if (typeof navigator !== "undefined" && navigator.vibrate) {
      try { navigator.vibrate(12); } catch { /* ignore */ }
    }
    // Fire the bounce animation
    setPopping(true);
    setTimeout(() => setPopping(false), 650);
  };

  const off = product.compare_at_price && product.compare_at_price > product.price
    ? Math.round(((product.compare_at_price - product.price) / product.compare_at_price) * 100)
    : 0;

  return (
    <Link to={`/product/${product.slug}`} className="card-hover group rounded-2xl bg-card border border-white/5 overflow-hidden flex flex-col" data-testid={`product-card-${product.product_id}`}>
      <div className="relative aspect-square overflow-hidden bg-secondary/40">
        <img src={(product.images || [])[0]} alt={product.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        {off > 0 && <span className="absolute top-3 left-3 chip bg-primary/90 text-primary-foreground border-transparent font-bold">-{off}%</span>}
        {isBestSeller && (
          <span className="absolute top-3 left-3 chip bg-black/80 text-primary border-primary/40 font-semibold" style={{ transform: off > 0 ? "translateY(28px)" : "none" }} data-testid={`bestseller-${product.product_id}`}>
            <Trophy className="h-3 w-3" fill="currentColor" /> Best seller
          </span>
        )}
        <button onClick={toggleWish} className="absolute top-3 right-3 h-9 w-9 rounded-full bg-black/60 backdrop-blur flex items-center justify-center hover:bg-primary hover:text-primary-foreground" data-testid={`wishlist-btn-${product.product_id}`}>
          <Heart className={`h-4 w-4 ${wish ? "fill-current" : ""}`} />
        </button>
      </div>
      <div className="p-4 flex-1 flex flex-col gap-1">
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{product.brand}</p>
        <p className="text-sm font-medium line-clamp-2">{product.title}</p>
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <Star className="h-3 w-3 text-primary" fill="currentColor" /> {product.rating?.toFixed(1) || "4.5"} <span>·</span> {product.reviews_count || 0} reviews
        </div>
        <div className="mt-auto pt-2 flex items-baseline gap-2">
          <span className="text-lg font-bold">${product.price?.toFixed(2)}</span>
          {product.compare_at_price && product.compare_at_price > product.price && <span className="text-xs text-muted-foreground line-through">${product.compare_at_price.toFixed(2)}</span>}
        </div>
        <button
          onClick={handleAdd}
          className={`btn-primary mt-3 h-10 rounded-lg text-sm ${popping ? "animate-pop animate-ring" : ""}`}
          data-testid={`add-to-cart-${product.product_id}`}
        >Add to cart</button>
      </div>
    </Link>
  );
}
